import React, { useEffect, useMemo, useRef, useState } from 'react'
import AnimatedCounter from '../AnimatedCounter/AnimatedCounter'
import useGitHubData from './useGitHubData'
import './GitHubActivity.scss'

const USERNAME = 'PRedemption'
const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', '']

const formatDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })

const timeAgo = (iso) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days < 1) return 'today'
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  const years = Math.floor(days / 365)
  return `${years}y ago`
}

// One floating tooltip shared by every mark inside a chart.
const useTooltip = () => {
  const [tip, setTip] = useState(null)
  const hostRef = useRef(null)
  const show = (event, content) => {
    const host = hostRef.current.getBoundingClientRect()
    const mark = event.currentTarget.getBoundingClientRect()
    setTip({ x: mark.left - host.left + mark.width / 2, y: mark.top - host.top, content })
  }
  const hide = () => setTip(null)
  const node = tip && (
    <div className="gh-tip" style={{ left: tip.x, top: tip.y }} role="status">{tip.content}</div>
  )
  return { hostRef, show, hide, node }
}

const Heatmap = ({ days }) => {
  const scrollRef = useRef(null)
  const { hostRef, show, hide, node } = useTooltip()

  // Pad the first week so every column runs Sunday → Saturday.
  const { cells, months } = useMemo(() => {
    const first = new Date(`${days[0].date}T00:00:00`).getDay()
    const padded = [...Array(first).fill(null), ...days]
    const monthLabels = []
    for (let col = 0; col * 7 < padded.length; col += 1) {
      const day = padded.slice(col * 7, col * 7 + 7).find(Boolean)
      if (day && Number(day.date.slice(8)) <= 7) {
        monthLabels.push({ col, label: new Date(`${day.date}T00:00:00`).toLocaleString('en', { month: 'short' }) })
      }
    }
    return { cells: padded, months: monthLabels }
  }, [days])

  // Most recent weeks matter most, so start scrolled to the end on narrow screens.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [])

  const columns = Math.ceil(cells.length / 7)

  return (
    <div className="gh-heat" ref={hostRef}>
      <div className="gh-heat__scroll" ref={scrollRef}>
        <div className="gh-heat__months" style={{ gridTemplateColumns: `repeat(${columns}, var(--cell))` }} aria-hidden="true">
          {months.map((month) => <span key={`${month.col}-${month.label}`} style={{ gridColumn: month.col + 1 }}>{month.label}</span>)}
        </div>
        <div className="gh-heat__body">
          <div className="gh-heat__days" aria-hidden="true">{DAY_LABELS.map((label, i) => <span key={i}>{label}</span>)}</div>
          <div className="gh-heat__grid" style={{ gridTemplateColumns: `repeat(${columns}, var(--cell))` }} role="img" aria-label={`Contribution heatmap for the last ${days.length} days`}>
            {cells.map((day, index) => (
              day ? (
                <span
                  key={day.date}
                  className={`gh-cell lvl-${day.level}`}
                  style={{ '--c': Math.floor(index / 7) }}
                  onMouseEnter={(event) => show(event, <><strong>{day.count} contribution{day.count === 1 ? '' : 's'}</strong><span>{formatDate(day.date)}</span></>)}
                  onMouseLeave={hide}
                />
              ) : <span key={`pad-${index}`} className="gh-cell is-pad" />
            ))}
          </div>
        </div>
      </div>
      <div className="gh-heat__legend" aria-hidden="true">
        <span>Less</span>
        {[0, 1, 2, 3, 4].map((level) => <span key={level} className={`gh-cell lvl-${level}`} />)}
        <span>More</span>
      </div>
      {node}
    </div>
  )
}

const BarChart = ({ data, label }) => {
  const { hostRef, show, hide, node } = useTooltip()
  const max = Math.max(1, ...data.map((item) => item.count))
  const peak = data.reduce((a, b) => (b.count > a.count ? b : a), data[0])

  return (
    <div className="gh-bars" ref={hostRef}>
      <div className="gh-bars__plot" role="list" aria-label={label}>
        {data.map((item, index) => (
          <div className="gh-bars__col" key={item.key || item.label} role="listitem">
            {item === peak && item.count > 0 && <span className="gh-bars__peak">{item.count}</span>}
            <button
              type="button"
              className={`gh-bar ${item === peak && item.count > 0 ? 'is-peak' : ''}`}
              style={{ '--h': `${(item.count / max) * 100}%`, '--i': index }}
              aria-label={`${item.full || item.label}: ${item.count} contributions`}
              onMouseEnter={(event) => show(event, <><strong>{item.count}</strong><span>{item.full || item.label}</span></>)}
              onFocus={(event) => show(event, <><strong>{item.count}</strong><span>{item.full || item.label}</span></>)}
              onMouseLeave={hide}
              onBlur={hide}
            />
            <span className="gh-bars__label">{item.label}</span>
          </div>
        ))}
      </div>
      {node}
    </div>
  )
}

const Skeleton = () => (
  <div className="gh-skeleton" aria-label="Loading GitHub activity">
    <div className="gh-stats">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="gh-stat gh-shimmer" />)}</div>
    <div className="gh-card gh-shimmer" style={{ height: 200 }} />
  </div>
)

const GitHubActivity = () => {
  const data = useGitHubData(USERNAME)
  const [showTable, setShowTable] = useState(false)

  const renderBody = () => {
    if (data.status === 'loading') return <Skeleton />
    if (data.status === 'error') {
      return (
        <div className="gh-card gh-error">
          <i className="fa-brands fa-github" />
          <p>GitHub is taking a breather right now (API rate limit). The full history is one click away.</p>
          <a href={`https://github.com/${USERNAME}`} target="_blank" rel="noreferrer">Open github.com/{USERNAME} <i className="fas fa-arrow-up-right-from-square" /></a>
        </div>
      )
    }

    const { summary, days, languages, recent, publicRepos, stars } = data
    const langMax = Math.max(1, ...languages.map((item) => item.count))
    const tiles = [
      summary && { value: summary.total, label: 'Contributions', sub: 'last 12 months' },
      summary && { value: summary.activeDays, label: 'Active days', sub: `${Math.round((summary.activeDays / days.length) * 100)}% of the year` },
      summary && { value: summary.longest, label: 'Longest streak', sub: 'consecutive days' },
      summary && { value: summary.current, label: 'Current streak', sub: summary.current ? 'and counting' : 'next commit starts it' },
      { value: publicRepos, label: 'Public repos', sub: stars ? `${stars} star${stars === 1 ? '' : 's'} earned` : 'on GitHub' }
    ].filter(Boolean)

    return (
      <>
        <div className="gh-stats">
          {tiles.map((tile, index) => (
            <div className="gh-stat" key={tile.label} data-reveal style={{ '--delay': `${index * 70}ms` }}>
              <AnimatedCounter end={tile.value} duration={1400} />
              <strong>{tile.label}</strong>
              <span>{tile.sub}</span>
            </div>
          ))}
        </div>

        {summary && (
          <div className="gh-card" data-reveal>
            <div className="gh-card__head">
              <h3><i className="fas fa-fire" /> Contribution heatmap</h3>
              {summary.best.date && <span>Best day: <strong>{summary.best.count}</strong> on {formatDate(summary.best.date)}</span>}
            </div>
            <Heatmap days={days} />
          </div>
        )}

        <div className="gh-grid">
          {summary && (
            <div className="gh-card" data-reveal style={{ '--delay': '60ms' }}>
              <div className="gh-card__head">
                <h3>By weekday</h3>
                <span>Busiest: <strong>{summary.busiest.label}</strong></span>
              </div>
              <BarChart data={summary.weekday} label="Contributions by weekday" />
            </div>
          )}
          {summary && (
            <div className="gh-card" data-reveal style={{ '--delay': '120ms' }}>
              <div className="gh-card__head">
                <h3>By month</h3>
                <span>Last 12 months</span>
              </div>
              <BarChart data={summary.months.map((m) => ({ ...m, full: `${m.label} ${m.year}` }))} label="Contributions by month" />
            </div>
          )}
          {languages.length > 0 && (
            <div className="gh-card" data-reveal style={{ '--delay': '180ms' }}>
              <div className="gh-card__head">
                <h3>Languages</h3>
                <span>{data.ownRepoCount} original repos</span>
              </div>
              <ul className="gh-langs">
                {languages.map((lang, index) => (
                  <li key={lang.name} style={{ '--w': `${(lang.count / langMax) * 100}%`, '--i': index }}>
                    <span className="gh-langs__name">{lang.name}</span>
                    <span className="gh-langs__track"><span /></span>
                    <span className="gh-langs__count">{lang.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {summary && (
          <div className="gh-table-toggle">
            <button type="button" onClick={() => setShowTable((value) => !value)} aria-expanded={showTable}>
              <i className={`fas ${showTable ? 'fa-chart-column' : 'fa-table'}`} /> {showTable ? 'Hide data table' : 'View as table'}
            </button>
            {showTable && (
              <div className="gh-tables">
                <table>
                  <caption>Contributions by weekday</caption>
                  <tbody>{summary.weekday.map((row) => <tr key={row.label}><th scope="row">{row.label}</th><td>{row.count}</td></tr>)}</tbody>
                </table>
                <table>
                  <caption>Contributions by month</caption>
                  <tbody>{summary.months.map((row) => <tr key={row.key}><th scope="row">{row.label} {row.year}</th><td>{row.count}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {recent.length > 0 && (
          <>
            <h3 className="gh-subhead" data-reveal>Recently active repositories</h3>
            <div className="gh-repos">
              {recent.map((repo, index) => (
                <a className="gh-repo" key={repo.name} href={repo.url} target="_blank" rel="noreferrer" data-reveal style={{ '--delay': `${index * 60}ms` }}>
                  <div className="gh-repo__top">
                    <i className="fas fa-book-bookmark" />
                    <strong>{repo.name}</strong>
                    <i className="fas fa-arrow-up-right-from-square gh-repo__out" />
                  </div>
                  <p>{repo.description || 'No description yet.'}</p>
                  <div className="gh-repo__meta">
                    {repo.language && <span><span className="gh-repo__dot" /> {repo.language}</span>}
                    <span><i className="far fa-star" /> {repo.stars}</span>
                    <span><i className="fas fa-clock-rotate-left" /> {timeAgo(repo.pushedAt)}</span>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <section id="github" className="pf-section pf-section--alt gh-section">
      <div className="pf-container">
        <header className="pf-heading gh-heading" data-reveal>
          <p className="pf-eyebrow"><span>05</span> Open source</p>
          <h2>Live from <span className="pf-gradient-text">GitHub</span>.</h2>
          <p>Pulled straight from the GitHub API: my public contribution history, how commits are spread across the week and year, and what I&apos;ve pushed to lately. Client and company work lives in private repositories.</p>
        </header>
        {renderBody()}
        <a className="gh-profile" href={`https://github.com/${USERNAME}`} target="_blank" rel="noreferrer" data-magnetic>
          <i className="fa-brands fa-github" /> Follow @{USERNAME} on GitHub <i className="fas fa-arrow-right" />
        </a>
      </div>
    </section>
  )
}

export default GitHubActivity
